import type { Term } from './terms.js'

/**
 * Подписи слотов создания персонажа и тех вариантов, у которых нет
 * записи контента.
 *
 * Отдельно от `terms.ts` по существу, а не для порядка. `terms.ts` —
 * про **записи**: у каждой есть идентификатор, и перевод привязан
 * к нему. Здесь же две вещи, у которых записи нет:
 *
 *   1. заголовки слотов. «Skill Proficiencies» — строка таблицы
 *      источника, приходящая с сервера полем `name`; ключ слота
 *      (`class.skills`) устойчив, а имя — нет;
 *   2. варианты-литералы. «Drow», «Black», «Cloud’s Jaunt» —
 *      это значения выбора, а не записи (`content/choice.ts`),
 *      и одинаковое слово в разных слотах значит разное: «Abyssal»
 *      в наследии тифлинга и «Abyssal» среди языков — не одно и то же.
 *
 * Из-за второго перевод варианта-литерала ищется **в своём слоте**,
 * а не в общем словаре: иначе наследие тифлинга называлось бы именем
 * языка. Варианты со ссылкой на запись (`srd:athletics`) сюда
 * не попадают вовсе — их переводит `terms.ts`.
 *
 * Полнота проверяется не списком в тесте, а планом, собранным
 * из настоящего контента: `slots.test.ts` прогоняет `creationPlan`
 * по всем классам, предысториям и видам и требует, чтобы ни одна
 * подпись на экране не осталась английской.
 */

/**
 * Ключ слота в словаре: точки заменены подчёркиванием.
 *
 * i18next разбирает точку как вложенность, а `slot.class` —
 * одновременно и заголовок, и начало `slot.class.skills`. Ветка
 * не может быть и строкой, и объектом, поэтому ключ плоский.
 */
export function slotDictKey(slotKey: string): string {
  return slotKey.replace(/\./g, '_')
}

/**
 * Заголовки слотов. Ключ — ключ слота, а не имя из источника: имя
 * приходит с сервера и меняется вместе с записью, ключ — нет.
 *
 * `equipment` берёт имя источника подстановкой: слот называется
 * «Стартовое снаряжение: Воин», и «Воин» — уже переведённое имя
 * записи, а не второй перевод класса.
 */
export const SLOT_TITLES: Readonly<Record<string, Term>> = {
  class: { ru: 'Класс', en: 'Class' },
  background: { ru: 'Предыстория', en: 'Background' },
  species: { ru: 'Вид', en: 'Species' },
  class_skills: { ru: 'Владение навыками', en: 'Skill Proficiencies' },
  class_tool: { ru: 'Владение инструментами', en: 'Tool Proficiencies' },
  background_tool: { ru: 'Владение инструментами', en: 'Tool Proficiencies' },
  class_equipment: {
    ru: 'Стартовое снаряжение: {{source}}',
    en: 'Starting Equipment: {{source}}',
  },
  background_equipment: {
    ru: 'Стартовое снаряжение: {{source}}',
    en: 'Starting Equipment: {{source}}',
  },
  rules_languages: { ru: 'Языки', en: 'Languages' },
  class_cantrips: { ru: 'Заговоры', en: 'Cantrips' },
  // Имя слота — слово самого класса из записи («Prepared Spells»).
  class_spells: { ru: 'Подготовленные заклинания', en: 'Prepared Spells' },
  species_draconic_ancestry: { ru: 'Драконье наследие', en: 'Draconic Ancestry' },
  species_elven_lineage: { ru: 'Эльфийская линия', en: 'Elven Lineage' },
  species_fiendish_legacy: { ru: 'Наследие исчадий', en: 'Fiendish Legacy' },
  species_giant_ancestry: { ru: 'Наследие великанов', en: 'Giant Ancestry' },
  species_gnomish_lineage: { ru: 'Гномья линия', en: 'Gnomish Lineage' },
  species_skillful: { ru: 'Умелость', en: 'Skillful' },
  species_versatile: { ru: 'Разносторонность', en: 'Versatile' },
  // Слот второго уровня: его объявила черта, выбранная игроком
  // (D58). Префикс ключа — ключ записи, а не роль: роли у неё нет.
  skilled_proficiency: { ru: 'Владение навыками или инструментами', en: 'Skills or Tools' },
}

/**
 * Варианты-литералы по слотам: слот → значение → перевод.
 *
 * Английское название лежит рядом и сверяется тестом с меткой
 * варианта в контенте — по той же причине, по какой это сделано
 * в `terms.ts`: второго источника имени быть не должно.
 */
export const SLOT_OPTIONS: Readonly<Record<string, Readonly<Record<string, Term>>>> = {
  species_draconic_ancestry: {
    black: { ru: 'Чёрный', en: 'Black' },
    blue: { ru: 'Синий', en: 'Blue' },
    brass: { ru: 'Латунный', en: 'Brass' },
    bronze: { ru: 'Бронзовый', en: 'Bronze' },
    copper: { ru: 'Медный', en: 'Copper' },
    gold: { ru: 'Золотой', en: 'Gold' },
    green: { ru: 'Зелёный', en: 'Green' },
    red: { ru: 'Красный', en: 'Red' },
    silver: { ru: 'Серебряный', en: 'Silver' },
    white: { ru: 'Белый', en: 'White' },
  },
  species_elven_lineage: {
    drow: { ru: 'Дроу', en: 'Drow' },
    high_elf: { ru: 'Высший эльф', en: 'High Elf' },
    wood_elf: { ru: 'Лесной эльф', en: 'Wood Elf' },
  },
  species_fiendish_legacy: {
    abyssal: { ru: 'Бездна', en: 'Abyssal' },
    chthonic: { ru: 'Хтонь', en: 'Chthonic' },
    infernal: { ru: 'Преисподняя', en: 'Infernal' },
  },
  species_giant_ancestry: {
    clouds_jaunt: {
      ru: 'Прыжок облачного великана',
      en: 'Cloud’s Jaunt (Cloud Giant)',
    },
    fires_burn: { ru: 'Пламя огненного великана', en: 'Fire’s Burn (Fire Giant)' },
    frosts_chill: { ru: 'Холод ледяного великана', en: 'Frost’s Chill (Frost Giant)' },
    hills_tumble: {
      ru: 'Опрокидывание холмового великана',
      en: 'Hill’s Tumble (Hill Giant)',
    },
    stones_endurance: {
      ru: 'Стойкость каменного великана',
      en: 'Stone’s Endurance (Stone Giant)',
    },
    storms_thunder: {
      ru: 'Гром штормового великана',
      en: 'Storm’s Thunder (Storm Giant)',
    },
  },
  species_gnomish_lineage: {
    forest_gnome: { ru: 'Лесной гном', en: 'Forest Gnome' },
    rock_gnome: { ru: 'Скальный гном', en: 'Rock Gnome' },
  },
}

/**
 * Позиции стартовых наборов, у которых название в наборе не совпадает
 * с названием записи предмета.
 *
 * Ключ — само английское название, приведённое к машинному виду.
 * Это единственный словарь, где ключ не идентификатор, и причина
 * ровно одна: у этих строк идентификатора нет. «Arcane Focus (orb)» —
 * это запись `srd:arcane_focus` плюс уточнение, которого в записи нет;
 * «Spellbook» — вовсе не запись: книги заклинаний в таблицах
 * снаряжения SRD нет (`content/srd/REPORT.md`, раздел 6.3).
 *
 * Опасность такого ключа известна: поменяется формулировка
 * в источнике — перевод отвалится молча. Поэтому его сторожит тест,
 * который проходит по всем наборам контента и требует перевода
 * каждой позиции.
 *
 * Множественное число сюда не попадает: «Daggers» переводится записью
 * `srd:dagger`, а два кинжала показывает количество рядом.
 */
export const EQUIPMENT_NAMES: Readonly<Record<string, Term>> = {
  arcane_focus_crystal: {
    ru: 'Магическая фокусировка (кристалл)',
    en: 'Arcane Focus (crystal)',
  },
  arcane_focus_orb: { ru: 'Магическая фокусировка (шар)', en: 'Arcane Focus (orb)' },
  arcane_focus_quarterstaff: {
    ru: 'Магическая фокусировка (боевой посох)',
    en: 'Arcane Focus (Quarterstaff)',
  },
  artisans_tools_or_musical_instrument_chosen_for_the_tool_proficiency_above: {
    ru: 'Инструменты ремесленника или музыкальный инструмент, выбранные выше',
    en: 'Artisan’s Tools or Musical Instrument chosen for the tool proficiency above',
  },
  book_history: { ru: 'Книга (по истории)', en: 'Book (history)' },
  book_occult_lore: { ru: 'Книга (по оккультизму)', en: 'Book (occult lore)' },
  book_prayers: { ru: 'Книга (молитвенник)', en: 'Book (prayers)' },
  druidic_focus_quarterstaff: {
    ru: 'Друидическая фокусировка (боевой посох)',
    en: 'Druidic Focus (Quarterstaff)',
  },
  druidic_focus_sprig_of_mistletoe: {
    ru: 'Друидическая фокусировка (ветка омелы)',
    en: 'Druidic Focus (sprig of mistletoe)',
  },
  gaming_set_same_as_above: {
    ru: 'Игровой набор (тот же, что выше)',
    en: 'Gaming Set (same as above)',
  },
  musical_instrument_of_your_choice: {
    ru: 'Музыкальный инструмент на выбор',
    en: 'Musical Instrument of your choice',
  },
  parchment_10_sheets: { ru: 'Пергамент (10 листов)', en: 'Parchment (10 sheets)' },
  parchment_8_sheets: { ru: 'Пергамент (8 листов)', en: 'Parchment (8 sheets)' },
  spellbook: { ru: 'Книга заклинаний', en: 'Spellbook' },
}

/**
 * Название позиции набора → ключ словаря: строчные буквы, всё
 * остальное — подчёркивание. «Arcane Focus (orb)» → `arcane_focus_orb`.
 */
export function equipmentNameKey(name: string): string {
  return (
    name
      .toLowerCase()
      // Апостроф выбрасывается, а не заменяется подчёркиванием: то же
      // правило, по которому именуются записи контента («Alchemist’s
      // Supplies» → `alchemists_supplies`, `content/srd/REPORT.md`,
      // раздел 3.0). Одно правило на весь проект, а не два похожих.
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
  )
}

function pick(terms: Readonly<Record<string, Term>>, language: 'ru' | 'en') {
  return Object.fromEntries(
    Object.entries(terms).map(([key, term]) => [key, term[language]]),
  )
}

/** Ветка `slot` для i18next. */
export function slotTitlesFor(language: 'ru' | 'en'): Record<string, string> {
  return pick(SLOT_TITLES, language)
}

/** Ветка `slotOption` для i18next: два уровня, слот и значение. */
export function slotOptionsFor(
  language: 'ru' | 'en',
): Record<string, Record<string, string>> {
  return Object.fromEntries(
    Object.entries(SLOT_OPTIONS).map(([slot, options]) => [
      slot,
      pick(options, language),
    ]),
  )
}

/** Ветка `equipment` для i18next. */
export function equipmentNamesFor(language: 'ru' | 'en'): Record<string, string> {
  return pick(EQUIPMENT_NAMES, language)
}
